import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { InfoCard } from "@/components/LegalPage";
import {
  ShieldCheck, Database, Share2, KeyRound, Cookie, HardDrive, Baby, Globe2,
  MapPin, MessageCircle, Info, Lock, EyeOff,
} from "lucide-react";

export default function Privacy() {
  const sections = [
    {
      id: "overview", title: "Overview", icon: Info,
      body: (
        <>
          <p>
            This Privacy Policy explains what MystiCast collects, why we collect it, and how we protect it.
            We keep this document plain-language on purpose — you deserve to understand what happens with your data.
          </p>
          <p>
            By using the Service you agree to the practices described here. If you don&apos;t, please stop using MystiCast
            and email <a href="mailto:privacy@mysticast.tv">privacy@mysticast.tv</a> so we can help delete your data.
          </p>
        </>
      ),
    },
    {
      id: "information-we-collect", title: "Information We Collect", icon: Database, highlight: "cyan",
      body: (
        <>
          <p>We collect the minimum required to run the platform. Nothing more.</p>
          <InfoCard icon={Lock} title="🔒 Account data" tone="volt">
            <p>Username, email, hashed password (bcrypt), date-of-birth for 18+ verification, and optional profile info you add — bio, avatar, cover image.</p>
          </InfoCard>
          <InfoCard icon={ShieldCheck} title="🛡 Verification data" tone="pink">
            <p>Identity verification is performed by <strong>Stripe Identity</strong>. We receive only a pass/fail signal — we <strong>do not</strong> store scans of your government-issued ID.</p>
          </InfoCard>
          <InfoCard icon={KeyRound} title="💳 Payments data" tone="orange">
            <p>Card and billing details are collected and stored by <strong>Stripe</strong>. MystiCast never sees or stores full card numbers.</p>
          </InfoCard>
          <InfoCard icon={Database} title="📊 Usage data" tone="cyan">
            <p>Streams watched, games played, chat messages, tips sent, and engagement events used to calculate Sparks and levels.</p>
          </InfoCard>
          <InfoCard icon={HardDrive} title="📱 Device data" tone="volt">
            <p>IP address, browser, device type, cookies, and similar identifiers used for security and troubleshooting.</p>
          </InfoCard>
        </>
      ),
    },
    {
      id: "how-information-is-used", title: "How Information Is Used", icon: Share2,
      body: (
        <>
          <p>Every piece of data serves a real, specific purpose:</p>
          <ul>
            <li>Operate, maintain, and improve the Service.</li>
            <li>Verify age and identity where required by law.</li>
            <li>Process purchases and streamer payouts.</li>
            <li>Detect and prevent fraud, abuse, and violations of our <Link to="/community">Community Guidelines</Link>.</li>
            <li>Send you important service notifications (security alerts, policy changes).</li>
            <li>Send marketing/newsletter only if you&apos;ve opted in — you can opt out anytime.</li>
          </ul>
        </>
      ),
    },
    {
      id: "sharing", title: "Who We Share With", icon: Share2,
      body: (
        <>
          <p><strong>We do not sell your personal information. Period.</strong></p>
          <p>We share data only in these narrow, specific cases:</p>
          <ul>
            <li><strong>Service providers</strong> under contract: Stripe (payments & identity), hosting/CDN, email delivery, analytics.</li>
            <li><strong>Other users</strong>: your public profile, posts marked public, and stream activity are visible to other users.</li>
            <li><strong>Legal & safety</strong>: when required by law or to protect the rights, safety, or property of MystiCast, our users, or the public.</li>
          </ul>
        </>
      ),
    },
    {
      id: "your-rights", title: "Your Rights", icon: ShieldCheck, highlight: "volt",
      body: (
        <>
          <InfoCard icon={EyeOff} title="⚖ Access, correction, deletion" tone="volt">
            <p>You can access, update, or delete your account and profile at any time in <Link to="/account">Account settings</Link>.</p>
          </InfoCard>
          <InfoCard icon={Lock} title="🔐 Post visibility" tone="cyan">
            <p>Every post can be Public, Friends-only, or Private. Your profile can be marked private so only accepted friends can see your content.</p>
          </InfoCard>
          <InfoCard icon={KeyRound} title="🗝 Account security" tone="pink">
            <p>Enable 2FA (TOTP) in Account &rarr; Security for stronger protection. Recovery codes are provided for emergencies.</p>
          </InfoCard>
          <p className="mt-4">
            To exercise any of these rights, use the settings above or contact us via the <Link to="/contact">contact page</Link>.
          </p>
        </>
      ),
    },
    {
      id: "cookies", title: "Cookies", icon: Cookie,
      body: (
        <p>
          We use a small number of cookies and browser-storage items to keep you signed in, remember preferences, and detect fraud.
          Full details are in our <Link to="/cookies">Cookie Policy</Link>.
        </p>
      ),
    },
    {
      id: "retention", title: "Data Retention", icon: HardDrive,
      body: (
        <>
          <p>
            We retain account information while your account is active. Deleted accounts are soft-purged after 30 days;
            chat messages, purchase records, and safety-related logs may be retained longer as required by law
            or for fraud prevention.
          </p>
        </>
      ),
    },
    {
      id: "security", title: "Security", icon: Lock,
      body: (
        <>
          <p>Passwords are hashed with bcrypt. Sessions use signed JWT tokens. Files are stored in encrypted object storage. Traffic is served over TLS.</p>
          <p>
            No system is ever perfectly secure — please use a unique password, enable 2FA,
            and let us know immediately if you spot anything suspicious.
          </p>
        </>
      ),
    },
    {
      id: "children", title: "Children", icon: Baby,
      body: (
        <p>
          MystiCast is <strong>18+</strong>. We do not knowingly collect data from anyone under 18. If we discover we have, we delete it and report as required.
        </p>
      ),
    },
    {
      id: "international", title: "International Transfers", icon: Globe2,
      body: (
        <p>
          Your data may be processed in the United States and other countries. Where applicable we rely on standard contractual clauses and equivalent safeguards.
        </p>
      ),
    },
    {
      id: "regional", title: "Regional Notices", icon: MapPin,
      body: (
        <>
          <h3>EU / EEA / UK</h3>
          <p>
            We process your data under GDPR/UK-GDPR. Legal bases include contract, consent (for marketing), legitimate interests (for security and fraud prevention),
            and compliance with legal obligations.
          </p>
          <h3>California</h3>
          <p>
            Under CPRA you have rights to know, delete, correct, and opt out of &quot;sale&quot; or &quot;sharing&quot;.
            MystiCast does not sell personal information.
          </p>
        </>
      ),
    },
    {
      id: "contact", title: "Contact", icon: MessageCircle,
      body: (
        <p>Privacy questions? Reach out via the <Link to="/contact">contact page</Link> or email <a href="mailto:privacy@mysticast.tv">privacy@mysticast.tv</a>.</p>
      ),
    },
  ];

  return (
    <LegalPage
      icon={ShieldCheck}
      label="PRIVACY POLICY"
      title="Privacy Policy"
      intro="We believe your privacy belongs to you. This policy explains what information MystiCast collects, why we collect it, and how we protect it."
      updated="February 2026"
      sections={sections}
      trustCard={{
        kicker: "OUR COMMITMENT",
        title: "Your attention belongs to creators — not advertisers.",
        points: [
          "MystiCast does not interrupt your experience with intrusive third-party advertising.",
          "MystiCast does not sell your personal information to advertising networks.",
          "Our platform is supported by creator-focused features, memberships, and platform services — never targeted ads.",
          "We build so that every scroll, click, and stream serves you and the creators you love — never a data broker.",
        ],
      }}
      contact={{
        title: "Questions about this policy?",
        body: "The MystiCast privacy team responds to every message. For urgent data requests (deletion, GDPR/CPRA), please indicate that in your subject line.",
        cta: { label: "Contact Privacy Team", to: "/contact" },
      }}
    />
  );
}
